import { IResourceCenter } from '../../../types/resourceCenter'
import { VideoPreview } from './VideoPreview'
import { prependBackendUrl } from '../../../gql/client'
import { isImageFile, isPdfFile, isVideoFile } from '../resourceTypeHelpers'
import { useIntl } from 'react-intl'
import { RESOURCE_MESSAGES as R_ } from '../resourceCenterMessages'

interface ResourceContentViewerProps {
  resource: IResourceCenter
  height?: number
  showDescription?: boolean
}

/**
 * Visor de contenido de un recurso (video embed para URLs, imagen/PDF inline,
 * o botón "abrir archivo"). Compartido entre ModalViewResource (Resource Center)
 * y el player de lecciones de la Academia.
 */
export const ResourceContentViewer: React.FC<ResourceContentViewerProps> = ({
  resource,
  height = 500,
  showDescription = true,
}) => {
  const intl = useIntl()
  const isUrl = !!resource.url
  const fileUrl = prependBackendUrl(resource.file || '')
  const fileName = resource.file?.split('/').pop()?.split('?')[0] || ''

  if (isUrl) {
    return (
      <div>
        <VideoPreview url={resource.url!} height={height} />
        {showDescription && resource.description && (
          <div className="mt-3">
            <strong>{intl.formatMessage(R_.description)}</strong>
            <p className="text-muted mb-0">{resource.description}</p>
          </div>
        )}
      </div>
    )
  }

  if (resource.file && isImageFile(fileName)) {
    return (
      <div className="text-center">
        <img
          src={fileUrl}
          alt={resource.name}
          style={{ maxWidth: '100%', maxHeight: height, objectFit: 'contain' }}
          className="rounded"
        />
        {showDescription && resource.description && (
          <div className="mt-3 text-start">
            <strong>{intl.formatMessage(R_.description)}</strong>
            <p className="text-muted mb-0">{resource.description}</p>
          </div>
        )}
      </div>
    )
  }

  if (resource.file && isVideoFile(fileName)) {
    return (
      <div>
        {/* Reproductor nativo del navegador — igual que un video de Loom/YouTube,
            no obliga a descargar el archivo para verlo */}
        <video
          key={fileUrl}
          src={fileUrl}
          controls
          preload="metadata"
          style={{ width: '100%', maxHeight: height, backgroundColor: '#000', borderRadius: 4 }}
        >
          {intl.formatMessage(R_.noVideoSupport)}
        </video>
        <div className="mt-2">
          <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="small text-muted">
            <i className="bi bi-download me-1" />
            {intl.formatMessage(R_.troublePlaying)}
          </a>
        </div>
        {showDescription && resource.description && (
          <div className="mt-3">
            <strong>{intl.formatMessage(R_.description)}</strong>
            <p className="text-muted mb-0">{resource.description}</p>
          </div>
        )}
      </div>
    )
  }

  if (resource.file && isPdfFile(fileName)) {
    return (
      <div>
        <iframe
          src={fileUrl}
          title={resource.name}
          style={{ width: '100%', height, border: 'none' }}
          className="rounded border"
        />
        {showDescription && resource.description && (
          <div className="mt-3">
            <strong>{intl.formatMessage(R_.description)}</strong>
            <p className="text-muted mb-0">{resource.description}</p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="text-center">
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2" />
        {intl.formatMessage(R_.fileResourceHint)}
      </div>
      {showDescription && resource.description && (
        <div className="mb-3 text-start">
          <strong>{intl.formatMessage(R_.description)}</strong>
          <p className="text-muted">{resource.description}</p>
        </div>
      )}
      <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
        <i className="bi bi-box-arrow-up-right me-2" />
        {intl.formatMessage(R_.openInNewTab)}
      </a>
    </div>
  )
}
